import api from '../lib/axios';
import type { ApiResponse } from '@maios/shared';

// --- Types ---

export interface IssueUser {
  id: string;
  name: string;
  // CHG-133 Step 6: email은 컨텍스트별 publicUserSelect 또는 memberWithEmailSelect — optional로 통일
  email?: string;
  avatarUrl: string | null;
  // CHG-133 Step 6: 표시 일관성
  displayColor?: string | null;
  nickname?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  profileVisibility?: Record<string, boolean>;
}

// CHG-041: 워크플로우 상태 정보
export interface IssueWorkflowStatus {
  statusId: string;
  name: string;
  color: string;
  category: 'TODO' | 'IN_PROGRESS' | 'DONE';
  sortOrder: number;
}

export interface IssueListItem {
  id: string;
  issueId: string;
  projectId: string;
  title: string;
  description: string | null;
  issueType: 'BUG' | 'TASK' | 'STORY' | 'EPIC' | 'SUB_TASK';
  issueTypeDef?: { issueTypeId: string; name: string; icon: string; color: string; customIconPath?: string | null } | null;
  priority: 'BLOCKER' | 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  statusId: string;
  workflowStatus: IssueWorkflowStatus;
  resolution: string | null;
  labels: string[];
  storyPoints: number | null;
  startDate: string | null;
  dueDate: string | null;
  parentIssueId: string | null;
  createdAt: string;
  updatedAt: string;
  reporter: IssueUser;
  assignee: IssueUser | null;
  parentIssue: { issueId: string; title: string } | null;
  _count: { comments: number; childIssues: number };
}

export interface IssueAttachment {
  attachmentId: string;
  issueId: string;
  originalName: string;
  storagePath: string;
  mimeType: string;
  fileSize: number;
  uploadedBy: string;
  createdAt: string;
  uploader: IssueUser;
  url: string;
}

export interface IssueDetail extends IssueListItem {
  comments: IssueComment[];
  // REQ-V24-008: expanded parentIssue (overrides IssueListItem)
  parentIssue: { id: string; issueId: string; title: string; issueType: string; issueTypeDef?: { issueTypeId: string; name: string; icon: string; color: string; customIconPath?: string | null } | null } | null;
  // REQ-V24-009: child issues (CHG-041: workflowStatus)
  childIssues?: { id: string; issueId: string; title: string; issueType: string; statusId: string; workflowStatus: IssueWorkflowStatus; issueTypeDef?: { issueTypeId: string; name: string; icon: string; color: string; customIconPath?: string | null } | null }[];
  linkedRequirement: { id: string; reqId: string; title: string } | null;
  linkedSpec: { id: string; functionCode: string; title: string } | null;
  linkedTestCase: { id: string; tcId: string; title: string; status: string } | null;
  issueAttachments?: IssueAttachment[];
  // CHG-123 REQ-V123-052: 채팅 메시지 출처
  sourceMessageId?: string | null;
  sourceMessage?: {
    messageId: string;
    content: string;
    createdAt: string;
    isDeleted: boolean;
    sender: { id: string; name: string; avatarUrl: string | null };
    channelId: string | null;
    channel: {
      id: string;
      name: string | null;
      type: 'PROJECT' | 'USER' | 'DM';
      isArchived: boolean;
      projectId: string | null;
      project: { projectKey: string } | null;
    } | null;
  } | null;
}

export interface IssueComment {
  id: string;
  content: string;
  createdAt: string;
  updatedAt?: string;
  author: IssueUser;
  parentCommentId?: string | null;
  reactions?: { emoji: string; userId: string }[];
}

export interface IssuePagination {
  page: number;
  size: number;
  total: number;
  totalPages: number;
}

export interface IssueListResponse {
  items: IssueListItem[];
  pagination: IssuePagination;
}

export interface CreateIssueData {
  title: string;
  description?: string;
  issueType?: string;
  priority?: string;
  assigneeId?: string;
  startDate?: string;
  dueDate?: string;
  parentIssueId?: string | null;
  labels?: string[];
  storyPoints?: number;
  linkedRequirementId?: string;
  linkedSpecId?: string;
  linkedTestCaseId?: string;
  customFields?: Record<string, unknown>;  // CHG-053
}

export interface UpdateIssueData {
  title?: string;
  description?: string | null;
  issueType?: string;
  priority?: string;
  assigneeId?: string | null;
  startDate?: string | null;
  dueDate?: string | null;
  parentIssueId?: string | null;
  labels?: string[];
  storyPoints?: number | null;
  resolution?: string | null;
  linkedRequirementId?: string | null;
  linkedSpecId?: string | null;
  linkedTestCaseId?: string | null;
  customFields?: Record<string, unknown>;  // CHG-053
}

export interface ActivityLogItem {
  id: string;
  action: string;
  entityType: string;
  entityId: string;
  before: Record<string, unknown> | null;
  after: Record<string, unknown> | null;
  createdAt: string;
  user: IssueUser;
}

export interface SavedFilter {
  id: string;
  userId: string;
  name: string;
  query: string;
  scope: 'PERSONAL' | 'SHARED';
  projectId: string | null;
  isFavorite: boolean;
  filterConfig?: unknown;
  createdAt: string;
  updatedAt: string;
}

// --- Issues API ---

export async function fetchIssues(
  projectId: string,
  params?: { filter?: string; page?: number; size?: number; sort?: string },
) {
  const query = new URLSearchParams();
  if (params?.filter) query.set('filter', params.filter);
  if (params?.page) query.set('page', String(params.page));
  if (params?.size) query.set('size', String(params.size));
  if (params?.sort) query.set('sort', params.sort);
  const qs = query.toString();
  const res = await api.get<ApiResponse<IssueListResponse>>(
    `/projects/${projectId}/issues${qs ? `?${qs}` : ''}`,
  );
  return res.data.data!;
}

export async function fetchIssue(projectId: string, issueId: string) {
  const res = await api.get<ApiResponse<IssueDetail>>(
    `/projects/${projectId}/issues/${issueId}`,
  );
  return res.data.data!;
}

export async function createIssue(projectId: string, data: CreateIssueData) {
  const res = await api.post<ApiResponse<IssueListItem>>(
    `/projects/${projectId}/issues`,
    data,
  );
  return res.data.data!;
}

// CHG-123 REQ-V123-010: 채팅 메시지로부터 이슈 생성
export interface CreateIssueFromMessageData {
  sourceMessageId: string;
  title: string;
  description?: string;
  issueTypeId?: string;
  priority?: string;
  assigneeId?: string;
  dueDate?: string;
  labels?: string[];
}

export async function createIssueFromMessage(projectId: string, data: CreateIssueFromMessageData) {
  const res = await api.post<ApiResponse<IssueListItem>>(
    `/projects/${projectId}/issues/from-message`,
    data,
  );
  return res.data.data!;
}

export async function updateIssue(projectId: string, issueId: string, data: UpdateIssueData) {
  const res = await api.put<ApiResponse<IssueListItem>>(
    `/projects/${projectId}/issues/${issueId}`,
    data,
  );
  return res.data.data!;
}

export async function deleteIssue(projectId: string, issueId: string) {
  await api.delete(`/projects/${projectId}/issues/${issueId}`);
}

export async function transitionIssue(
  projectId: string,
  issueId: string,
  statusId: string,
  resolution?: string,
) {
  const res = await api.put<ApiResponse<IssueListItem>>(
    `/projects/${projectId}/issues/${issueId}/transition`,
    { statusId, resolution },
  );
  return res.data.data!;
}

// --- Available Transitions (CHG-043: REQ-V43-016) ---

export interface AvailableTransitionItem {
  transitionId: string | null;
  name: string;
  toStatus: { statusId: string; name: string; color: string };
  isAllowed: boolean;
  disabledReason: string | null;
}

export interface AvailableTransitionsData {
  currentStatus: { statusId: string; name: string; color: string };
  availableTransitions: AvailableTransitionItem[];
}

export async function fetchAvailableTransitions(projectId: string, issueId: string) {
  const res = await api.get<ApiResponse<AvailableTransitionsData>>(
    `/projects/${projectId}/issues/${issueId}/available-transitions`,
  );
  return res.data.data!;
}

export async function addComment(
  projectId: string,
  issueId: string,
  content: string,
  parentCommentId?: string | null,
) {
  const res = await api.post<ApiResponse<IssueComment>>(
    `/projects/${projectId}/issues/${issueId}/comments`,
    { content, parentCommentId: parentCommentId ?? undefined },
  );
  return res.data.data!;
}

export async function updateComment(projectId: string, issueId: string, commentId: string, content: string) {
  const res = await api.put<ApiResponse<IssueComment>>(
    `/projects/${projectId}/issues/${issueId}/comments/${commentId}`,
    { content },
  );
  return res.data.data!;
}

export async function deleteComment(projectId: string, issueId: string, commentId: string) {
  await api.delete(`/projects/${projectId}/issues/${issueId}/comments/${commentId}`);
}

export async function addCommentReaction(projectId: string, issueId: string, commentId: string, emoji: string) {
  await api.post(`/projects/${projectId}/issues/${issueId}/comments/${commentId}/reactions`, { emoji });
}

export async function removeCommentReaction(projectId: string, issueId: string, commentId: string, emoji: string) {
  await api.delete(`/projects/${projectId}/issues/${issueId}/comments/${commentId}/reactions`, { data: { emoji } });
}

export async function fetchActivityLogs(projectId: string, issueId: string) {
  const res = await api.get<ApiResponse<ActivityLogItem[]>>(
    `/projects/${projectId}/issues/${issueId}/activity`,
  );
  return res.data.data!;
}

// --- Attachments API ---

export async function uploadAttachments(
  projectId: string,
  issueId: string,
  files: File[],
  onProgress?: (percent: number) => void,
) {
  const formData = new FormData();
  for (const file of files) {
    formData.append('files', file);
  }
  const res = await api.post<ApiResponse<IssueAttachment[]>>(
    `/projects/${projectId}/issues/${issueId}/attachments`,
    formData,
    {
      headers: { 'Content-Type': 'multipart/form-data' },
      onUploadProgress: (e) => {
        if (onProgress && e.total) {
          onProgress(Math.round((e.loaded * 100) / e.total));
        }
      },
    },
  );
  return res.data.data!;
}

export async function deleteAttachment(
  projectId: string,
  issueId: string,
  attachmentId: string,
) {
  await api.delete(
    `/projects/${projectId}/issues/${issueId}/attachments/${attachmentId}`,
  );
}

// --- Filters API ---

export async function fetchFilters(params?: { scope?: string; projectId?: string }) {
  const query = new URLSearchParams();
  if (params?.scope) query.set('scope', params.scope);
  if (params?.projectId) query.set('projectId', params.projectId);
  const qs = query.toString();
  const res = await api.get<ApiResponse<SavedFilter[]>>(`/filters${qs ? `?${qs}` : ''}`);
  return res.data.data!;
}

export async function createFilter(data: {
  name: string;
  query?: string;
  projectId?: string;
  scope?: string;
  filterConfig?: unknown;
}) {
  const res = await api.post<ApiResponse<SavedFilter>>('/filters', data);
  return res.data.data!;
}

export async function fetchFilter(filterId: string): Promise<SavedFilter> {
  const res = await api.get<ApiResponse<SavedFilter>>(`/filters/${filterId}`);
  return res.data.data!;
}

export async function updateFilter(filterId: string, data: {
  name?: string;
  query?: string;
  scope?: string;
  filterConfig?: unknown;
}): Promise<SavedFilter> {
  const res = await api.put<ApiResponse<SavedFilter>>(`/filters/${filterId}`, data);
  return res.data.data!;
}

export async function deleteFilter(filterId: string) {
  await api.delete(`/filters/${filterId}`);
}

export async function toggleFilterFavorite(filterId: string) {
  const res = await api.post<ApiResponse<SavedFilter>>(`/filters/${filterId}/favorite`);
  return res.data.data!;
}

// --- Issue Search API (REQ-V24-015) ---

export interface SearchIssueResult {
  id: string;
  issueId: string;
  title: string;
  issueType: string;
  statusId: string;
  workflowStatus: IssueWorkflowStatus;
  parentIssueId: string | null;
}

export async function searchIssues(projectId: string, q: string, exclude?: string) {
  const params = new URLSearchParams({ q });
  if (exclude) params.set('exclude', exclude);
  const res = await api.get<ApiResponse<SearchIssueResult[]>>(
    `/projects/${projectId}/issues/search?${params.toString()}`,
  );
  return res.data.data!;
}

// --- Issue Tree API (CHG-023: 타임라인) ---

export interface IssueTreeNode {
  issueId: string;
  title: string;
  issueType: string;
  issueTypeDef?: { issueTypeId: string; name: string; icon: string; color: string; customIconPath?: string | null } | null;
  statusId: string;
  workflowStatus: IssueWorkflowStatus;
  priority: string;
  startDate: string | null;
  dueDate: string | null;
  parentIssueId: string | null;
  assignee: IssueUser | null;
  children: IssueTreeNode[];
}

export async function fetchIssueTree(projectId: string) {
  const res = await api.get<ApiResponse<IssueTreeNode[]>>(
    `/projects/${projectId}/issues/tree`,
  );
  return res.data.data!;
}

// ── CHG-085: 글로벌 이슈 + 필터 옵션 ──

export interface GlobalIssueListResponse {
  items: unknown[];
  pagination: { total: number; page: number; size: number; totalPages: number };
}

export async function fetchGlobalIssues(params?: { filter?: string; page?: number; size?: number; sort?: string }): Promise<GlobalIssueListResponse> {
  const query = new URLSearchParams();
  if (params?.filter) query.set('filter', params.filter);
  if (params?.page) query.set('page', String(params.page));
  if (params?.size) query.set('size', String(params.size));
  if (params?.sort) query.set('sort', params.sort);
  const qs = query.toString();
  const res = await api.get<ApiResponse<GlobalIssueListResponse>>(`/issues${qs ? `?${qs}` : ''}`);
  return res.data.data!;
}

export interface FilterOptions {
  projects: { id: string; name: string; projectKey: string }[];
  assignees: { id: string; name: string; profileImage: string | null }[];
  issueTypes: { issueTypeId: string; name: string; icon: string; color: string }[];
  statuses: { statusId: string; name: string; color: string; category: string }[];
  priorities: string[];
}

export async function fetchFilterOptions(): Promise<FilterOptions> {
  const res = await api.get<ApiResponse<FilterOptions>>('/issues/filter-options');
  return res.data.data!;
}
